import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Curves } from './Curves';
import type { DrawnCurve } from './Curves';
import { boxAt, sCurve, sidePoint } from './curveGeometry';
import type { Box, Point, Side } from './curveGeometry';
import { linkKey, refKey, withLinkAdded } from './planeKey';
import type { PlaneLink, PlaneRef } from './planeKey';

/**
 * Connections on a canvas: drawn from a card's handle to another card, selected, and removed with
 * Delete or the × on a hovered one. The page says where its cards are drawn ([boxes], by key) and
 * how a change is written ([commit], with the ends the change joins, which a page that keeps cards
 * only once they are placed has to place first); this keeps the gesture and what is drawn.
 */
export function useLinks({
  boxes,
  links,
  refOf,
  canvasAt,
  commit,
  onPress,
}: {
  boxes: ReadonlyMap<string, Box>;
  links: readonly PlaneLink[];
  /** The ref of the card under [key], where it has one. */
  refOf: (key: string) => PlaneRef | null;
  canvasAt: (clientX: number, clientY: number) => Point;
  commit: (next: PlaneLink[], ends: PlaneRef[]) => void;
  /** A handle was pressed: whatever was open on the page lets go. */
  onPress?: () => void;
}) {
  const press = useRef<{ from: string; side: Side } | null>(null);
  const [linking, setLinking] = useState<{ from: string; side: Side; at: Point; target: string | null } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  /** A link is drawn while both its ends are on the canvas; taking one off hides its connections
   *  without deleting them. */
  const curves = useMemo(
    () =>
      links.flatMap((l): DrawnCurve[] => {
        const from = boxes.get(refKey(l.from));
        const to = boxes.get(refKey(l.to));
        return from && to ? [{ key: linkKey(l), from, to }] : [];
      }),
    [links, boxes],
  );

  const remove = useCallback((key: string) => commit(links.filter((l) => linkKey(l) !== key), []), [links, commit]);

  const onHandleDown = (key: string) => (side: Side) => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    onPress?.();
    setSelected(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    press.current = { from: key, side };
    setLinking({ from: key, side, at: canvasAt(event.clientX, event.clientY), target: null });
  };

  /** Moves a connection being drawn; false when none is, and the move is the page's. */
  const move = (event: ReactPointerEvent): boolean => {
    const l = press.current;
    if (!l) return false;
    const at = canvasAt(event.clientX, event.clientY);
    setLinking({ ...l, at, target: boxAt(boxes, at, l.from) });
    return true;
  };

  /** Lets go of a connection being drawn: on a card it connects, anywhere else nothing happens.
   *  False when none is. */
  const up = (event: ReactPointerEvent): boolean => {
    const l = press.current;
    if (!l) return false;
    press.current = null;
    setLinking(null);
    const target = boxAt(boxes, canvasAt(event.clientX, event.clientY), l.from);
    const from = refOf(l.from);
    const to = target ? refOf(target) : null;
    const next = from && to ? withLinkAdded(links, from, to) : null;
    if (next && from && to) commit(next, [from, to]);
    return true;
  };

  useEffect(() => {
    if (!selected) return;
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      event.preventDefault();
      remove(selected);
      setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, remove]);

  const from = linking && boxes.get(linking.from);
  const layer = (
    <>
      <Curves curves={curves} selected={selected} onSelect={setSelected} onRemove={remove} />
      {linking && from && (
        <svg className="edges" aria-hidden="true">
          <g>
            <path d={sCurve(sidePoint(from, linking.side), linking.at, linking.side).d} className="edge-draft" />
            <circle cx={linking.at.x} cy={linking.at.y} r="5" className="edge-draft-dot" />
          </g>
        </svg>
      )}
    </>
  );

  return {
    /** A connection is being drawn. */
    drawing: () => press.current !== null,
    selected,
    setSelected,
    /** The card a connection being drawn would land on. */
    target: linking?.target ?? null,
    onHandleDown,
    move,
    up,
    layer,
  };
}
